import { ComponentProps, forwardRef, useRef } from 'react';
import Link from '~/components/Link';
import Button from './Button';

// may someday need to support a discriminated `delta` prop
export type LinkButtonProps = Pick<
  ComponentProps<typeof Link>,
  'to' | 'target' | 'relative' | 'reloadDocument' | 'replace'
> &
  Omit<ComponentProps<typeof Button>, 'onClick'>;

/**
 * LinkButton component for buttons behaving similar to a link, just by means of navigating on click.
 * Applies some styling overrides appropriate for link-styled buttons.
 *
 * The behavior here is that it's a button which acts like a link, by delegating to one.
 *
 * If you're using this, reconsider if it can actually be a `<Link>` or `<Button>` for visual consistency and accessibility semantics.
 */
const LinkButton = forwardRef<HTMLButtonElement, LinkButtonProps>(
  (
    {
      // delegated link props
      to,
      target,
      relative,
      reloadDocument,
      replace,
      // button props
      ...props
    },
    ref,
  ) => {
    const linkRef = useRef<HTMLAnchorElement>(null);

    return (
      <>
        <Link
          ref={linkRef}
          hidden
          to={to}
          target={target}
          relative={relative}
          reloadDocument={reloadDocument}
          replace={replace}
          data-testid="link-button-delegate"
        />
        <Button
          ref={ref}
          onClick={() => linkRef.current?.click()}
          // oxlint-disable-next-line react/jsx-props-no-spreading
          {...props}
        />
      </>
    );
  },
);
LinkButton.displayName = 'LinkButton';

export default LinkButton;
